import { gql, useLazyQuery } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconError from '@apollo/icons/default/IconError.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import IconSync from '@apollo/icons/default/IconSync.svg';
import IconTelescope2 from '@apollo/icons/default/IconTelescope2.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Button,
  ButtonGroup,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  StatusBadge,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import semverGt from 'semver/functions/gt';
import semverMajor from 'semver/functions/major';
import semverPrerelease from 'semver/functions/prerelease';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  ChampionDashboard_UpdatesAvailableModal,
  ChampionDashboard_UpdatesAvailableModalVariables,
  ChampionDashboard_UpdatesAvailableModal_graph,
} from 'src/lib/graphqlTypes/types';
import { ApolloGitHubRouter, DocsRouter } from 'src/lib/routers';

import {
  PackageMetadata,
  UPDATE_SEVERITY,
  determineUpdateSeverityLevel,
  parseVersion,
} from './packageMetadata/packageMetadata';
import { severityComponents } from './SeverityComponents';

const lastUpdatedFormatString = 'MMMM D, YYYY [at] hh:mm:ss A';

interface UpdatesAvailableModalProps {
  orgName: string;
  graph: NonNullable<ChampionDashboard_UpdatesAvailableModal_graph>;
  isOpen: boolean;
  onClose: () => void;
  packageMetadata: PackageMetadata;
}

export const UpdatesAvailableModal = ({
  orgName,
  graph,
  isOpen,
  onClose,
  packageMetadata,
}: UpdatesAvailableModalProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [lastUpdated, setLastUpdated] = useState(
    moment(Date.now()).format(lastUpdatedFormatString),
  );
  const [refreshStatus, setRefreshStatus] = useState<
    'none' | 'refreshing' | 'refreshed-recently'
  >('none');

  const [refreshStats, { data, error }] = useLazyQuery(
    gql<
      ChampionDashboard_UpdatesAvailableModal,
      ChampionDashboard_UpdatesAvailableModalVariables
    >`
      query ChampionDashboard_UpdatesAvailableModal($graphId: ID!) {
        graph(id: $graphId) {
          id
          title
          statsWindow(from: "-86400") {
            billingUsageStats {
              timestamp
              groupBy {
                agentVersion
                variant: schemaTag
              }
            }
          }
        }
      }
    `,
    {
      variables: { graphId: graph.id },
      fetchPolicy: 'network-only',
    },
  );

  const { router: routerTableData, server: serverTableData } = useMemo(() => {
    // fallback to original graph data if an error occurs during a refetch
    const graphFromPropsOrRefresh = error ? graph : (data?.graph ?? graph);
    return reduceTableData(graphFromPropsOrRefresh, packageMetadata);
  }, [graph, data, error, packageMetadata]);

  const refreshButtonLabel =
    refreshStatus === 'refreshing'
      ? 'Refreshing'
      : refreshStatus === 'refreshed-recently'
        ? 'Refreshed'
        : 'Refresh';

  return (
    <Modal
      size="3xl"
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={closeButtonRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{`${orgName} (${graph.title})`}</ModalTitle>
          <ModalDescription className="flex items-center gap-1">
            <IconHistory className="size-4" />
            {lastUpdated}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {routerTableData.size > 0 && (
            <AgentVersionTable data={routerTableData} routerOrServer="router" />
          )}
          {serverTableData.size > 0 && (
            <AgentVersionTable data={serverTableData} routerOrServer="server" />
          )}
        </ModalBody>
        <ModalFooter>
          <span>
            {`View changelog${
              routerTableData.size > 0 && serverTableData.size > 0 ? 's' : ''
            } for `}
            {routerTableData.size > 0 && serverTableData.size > 0 && (
              <>
                <ChangelogLink routerOrServer="server">
                  Apollo Server
                </ChangelogLink>{' '}
                or{' '}
                <ChangelogLink routerOrServer="router">
                  Apollo Router
                </ChangelogLink>
              </>
            )}
            {serverTableData.size > 0 && !routerTableData.size && (
              <ChangelogLink routerOrServer="server">
                Apollo Server
              </ChangelogLink>
            )}
            {routerTableData.size > 0 && !serverTableData.size && (
              <ChangelogLink routerOrServer="router">
                Apollo Router
              </ChangelogLink>
            )}
          </span>
          <ButtonGroup>
            <IconButton
              aria-label={refreshButtonLabel}
              variant="secondary"
              title={refreshButtonLabel}
              isLoading={refreshStatus === 'refreshing'}
              icon={
                refreshStatus === 'refreshed-recently' ? (
                  <IconCheck />
                ) : (
                  <IconSync />
                )
              }
              isDisabled={
                refreshStatus === 'refreshing' ||
                refreshStatus === 'refreshed-recently'
              }
              onClick={async () => {
                setRefreshStatus('refreshing');
                // spin for at least a non-jarring amount of time, 800ms is arbitrary
                await Promise.all([
                  refreshStats(),
                  new Promise<void>((resolve) =>
                    setTimeout(() => resolve(), 800),
                  ),
                ]);

                // show check box icon and disable button for the next 5s
                setRefreshStatus('refreshed-recently');
                setLastUpdated(
                  moment(Date.now()).format(lastUpdatedFormatString),
                );
                setTimeout(() => setRefreshStatus('none'), 5000);
              }}
            />
            <Button ref={closeButtonRef} variant="primary" onClick={onClose}>
              Close
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

enum SUPPORT_LEVEL {
  GA,
  Prerelease,
  Deprecated,
  EOL,
}

interface AgentVersionData {
  latestVersion: string | null;
  versions: Set<string>;
  updateSeverity: UPDATE_SEVERITY | null;
  support: {
    level: SUPPORT_LEVEL | null;
    eol: Date | null;
  };
}

// This reducer aggregates stats data into the lowest severity update/support
// level for each variant to display in the detailed modal table
//
// We want the lowest sev _per variant_ so that users can see the effects of
// their updates as they start to report stats from an updated version.
function reduceTableData(
  graph: NonNullable<ChampionDashboard_UpdatesAvailableModal_graph>,
  packageMetadata: PackageMetadata,
): {
  router: Map<string, AgentVersionData>;
  server: Map<string, AgentVersionData>;
} {
  const stats =
    graph.statsWindow?.billingUsageStats.map(({ timestamp, groupBy }) => ({
      timestamp,
      variant: groupBy.variant ?? '',
      version: groupBy.agentVersion ?? '',
    })) ?? [];

  const tableData = stats.reduce(
    (acc, { variant, version }) => {
      const parsed = parseVersion(version);
      if (!parsed) return acc;
      const [packageName, versionString] = parsed;

      const variantMap =
        packageName === 'apollo-router' ? acc.router : acc.server;

      const entry = variantMap.get(variant) ?? {
        latestVersion: null,
        versions: new Set(),
        updateSeverity: null,
        support: {
          level: null,
          eol: null,
        },
      };
      if (!variantMap.has(variant)) {
        variantMap.set(variant, entry);
      }

      entry.versions.add(versionString);

      const severityLevel = determineUpdateSeverityLevel(
        packageName,
        versionString,
        packageMetadata,
      );

      const latestVersion = entry.latestVersion;
      // if incoming version is greater than current greatest, we need to update
      if (!latestVersion || semverGt(versionString, latestVersion)) {
        const majorVersion = semverMajor(versionString);
        const versionInfo = packageMetadata[packageName]?.byMajor[majorVersion];

        if (versionInfo) {
          const eol = versionInfo.eol ? new Date(versionInfo.eol) : null;
          entry.support = eol
            ? {
                level:
                  Number(eol) < Date.now()
                    ? SUPPORT_LEVEL.EOL
                    : SUPPORT_LEVEL.Deprecated,
                eol,
              }
            : semverPrerelease(versionString)
              ? { level: SUPPORT_LEVEL.Prerelease, eol: null }
              : { level: SUPPORT_LEVEL.GA, eol: null };
        }

        entry.latestVersion = versionString;
        entry.updateSeverity = severityLevel;
      }

      return acc;
    },
    {
      router: new Map<string, AgentVersionData>(),
      server: new Map<string, AgentVersionData>(),
    },
  );

  // sort router and server tables by severity
  tableData.router = new Map(
    [...tableData.router].sort(([, versionDataA], [, versionDataB]) =>
      (versionDataA.updateSeverity ?? UPDATE_SEVERITY.Unknown) <
      (versionDataB.updateSeverity ?? UPDATE_SEVERITY.Unknown)
        ? 1
        : -1,
    ),
  );
  tableData.server = new Map(
    [...tableData.server].sort(([, versionDataA], [, versionDataB]) =>
      (versionDataA.updateSeverity ?? UPDATE_SEVERITY.Unknown) <
      (versionDataB.updateSeverity ?? UPDATE_SEVERITY.Unknown)
        ? 1
        : -1,
    ),
  );

  return tableData;
}

interface TableProps {
  data: Map<string, AgentVersionData>;
  routerOrServer: 'router' | 'server';
}

const AgentVersionTable = ({ data, routerOrServer }: TableProps) => (
  <Table size="condensed">
    <Thead>
      <Tr>
        <Th>Variant</Th>
        <Th>
          <span className="capitalize">{routerOrServer}</span> version
        </Th>
        <Th>Updates available</Th>
        <Th>
          <span className="flex items-center gap-1">
            Support{' '}
            <Tooltip
              placement="left"
              className="w-auto"
              interactive
              label={
                <Link isExternal to={DocsRouter.path('LaunchStages')}>
                  Learn more about support stages
                </Link>
              }
            >
              <IconInfo className="size-4 text-secondary" />
            </Tooltip>
          </span>
        </Th>
      </Tr>
    </Thead>
    <Tbody>
      {[...data.entries()].map(([variant, agentVersion]) => {
        const severityComponent =
          severityComponents[
            agentVersion.updateSeverity ?? UPDATE_SEVERITY.Unknown
          ];

        return (
          <Tr key={variant}>
            <Td>{variant}</Td>
            <Td>
              {agentVersion.versions.size > 1 ? (
                <span className="flex items-center gap-1">
                  {`v${agentVersion.latestVersion}`}
                  <Tooltip
                    className="max-w-72"
                    label={
                      <span>
                        Additional older versions reporting to this variant in
                        the last 24 hours:{' '}
                        {[...agentVersion.versions]
                          .filter(
                            (version) => version !== agentVersion.latestVersion,
                          )
                          .map((version) => `v${version}`)
                          .join(', ')}
                      </span>
                    }
                  >
                    <IconWarning className="size-4 text-icon-change" />
                  </Tooltip>
                </span>
              ) : (
                `v${agentVersion.latestVersion}`
              )}
            </Td>
            <Td>
              <StatusBadge
                colorScheme={severityComponent.color}
                icon={<severityComponent.Icon />}
                variant="hidden"
              >
                {severityComponent.text}
              </StatusBadge>
            </Td>
            <Td>
              <SupportCell support={agentVersion.support} />
            </Td>
          </Tr>
        );
      })}
    </Tbody>
  </Table>
);

interface ChangelogLinkProps {
  routerOrServer: 'router' | 'server';
  children: string;
}

const ChangelogLink = ({ routerOrServer, children }: ChangelogLinkProps) => {
  return (
    <Link
      isExternal
      to={
        routerOrServer === 'router'
          ? ApolloGitHubRouter.path('RouterChangelog')
          : ApolloGitHubRouter.path('ServerChangelog')
      }
    >
      {children}
    </Link>
  );
};

interface SupportCellProps {
  support: AgentVersionData['support'];
}

const SupportCell = ({ support }: SupportCellProps) => {
  switch (support.level) {
    case SUPPORT_LEVEL.EOL:
      return (
        <StatusBadge colorScheme="red" icon={<IconError />} variant="hidden">
          Past EOL
        </StatusBadge>
      );
    case SUPPORT_LEVEL.Deprecated:
      return (
        <Tooltip
          label={<span>EOL: {formatEol(support.eol)}</span>}
          placement="left"
          className="w-44"
        >
          <StatusBadge
            colorScheme="yellow"
            icon={<IconWarning />}
            variant="hidden"
          >
            Deprecated
          </StatusBadge>
        </Tooltip>
      );
    case SUPPORT_LEVEL.Prerelease:
      return (
        <StatusBadge
          colorScheme="gray"
          icon={<IconTelescope2 />}
          variant="hidden"
        >
          Prerelease
        </StatusBadge>
      );
    case SUPPORT_LEVEL.GA:
      return (
        <StatusBadge
          colorScheme="green"
          icon={<IconSuccess />}
          variant="hidden"
        >
          Generally Available
        </StatusBadge>
      );
    default:
      Sentry.captureException(
        new Error(`Unexpected SUPPORT_LEVEL enum value detected: ${support}`),
      );
      return null;
  }
};

function formatEol(date: Date | null) {
  return moment(date).format('MMMM Do, YYYY');
}
